/**
 * Timezone utility functions for BetterMD
 * Handles timezone conversions for Sydney and Adelaide practitioners
 */

import { format, parseISO } from 'date-fns';
import { zonedTimeToUtc, utcToZonedTime, formatInTimeZone } from 'date-fns-tz';

// Australian timezone constants
export const TIMEZONES = {
  ADELAIDE: 'Australia/Adelaide',
  SYDNEY: 'Australia/Sydney',
  BRISBANE: 'Australia/Brisbane',
  PERTH: 'Australia/Perth',
  DARWIN: 'Australia/Darwin'
} as const;

export type TimezoneKey = keyof typeof TIMEZONES;
export type TimezoneValue = typeof TIMEZONES[TimezoneKey];

// Timezone display names
export const TIMEZONE_DISPLAY_NAMES: Record<TimezoneValue, string> = {
  [TIMEZONES.ADELAIDE]: 'Adelaide',
  [TIMEZONES.SYDNEY]: 'Sydney',
  [TIMEZONES.BRISBANE]: 'Brisbane',
  [TIMEZONES.PERTH]: 'Perth',
  [TIMEZONES.DARWIN]: 'Darwin'
};

// Default timezone (Adelaide)
export const DEFAULT_TIMEZONE = TIMEZONES.ADELAIDE;

/**
 * Convert a Date object to a specific timezone
 * @param date - The date to convert
 * @param timezone - Target timezone (e.g., 'Australia/Sydney')
 * @returns Date object adjusted to the target timezone
 */
export const convertToTimezone = (date: Date, timezone: TimezoneValue): Date => {
  return utcToZonedTime(date, timezone);
};

/**
 * Convert a date from a specific timezone to UTC
 * @param date - The date in the source timezone
 * @param timezone - Source timezone
 * @returns UTC Date object
 */
export const convertFromTimezone = (date: Date, timezone: TimezoneValue): Date => {
  return zonedTimeToUtc(date, timezone);
};

/**
 * Format a date in a specific timezone
 * @param date - The date to format
 * @param timezone - Target timezone
 * @param formatPattern - Format pattern (e.g., 'yyyy-MM-dd HH:mm')
 * @returns Formatted date string in the target timezone
 */
export const formatInTimezone = (
  date: Date,
  timezone: TimezoneValue,
  formatPattern: string = 'yyyy-MM-dd HH:mm'
): string => {
  return formatInTimeZone(date, timezone, formatPattern);
};

/**
 * Convert practitioner time to display time
 * Used when showing appointment times in the calendar
 * @param date - Date in practitioner's timezone
 * @param practitionerTimezone - Practitioner's timezone
 * @param displayTimezone - Timezone to display in (optional, defaults to practitioner's)
 * @returns Date object for display
 */
export const convertPractitionerTimeToDisplay = (
  date: Date,
  practitionerTimezone: TimezoneValue,
  displayTimezone: TimezoneValue = practitionerTimezone
): Date => {
  // If same timezone, no conversion needed
  if (practitionerTimezone === displayTimezone) {
    return date;
  }
  
  // Convert to UTC first, then to display timezone
  const utcDate = convertFromTimezone(date, practitionerTimezone);
  return convertToTimezone(utcDate, displayTimezone);
};

/**
 * Convert display time to practitioner time
 * Used when creating appointments from calendar selection
 * @param date - Date in display timezone
 * @param displayTimezone - Current display timezone
 * @param practitionerTimezone - Target practitioner's timezone
 * @returns Date object in practitioner's timezone
 */
export const convertDisplayTimeToPractitioner = (
  date: Date,
  displayTimezone: TimezoneValue,
  practitionerTimezone: TimezoneValue
): Date => {
  // If same timezone, no conversion needed
  if (displayTimezone === practitionerTimezone) {
    return date;
  }
  
  // Convert to UTC first, then to practitioner timezone
  const utcDate = convertFromTimezone(date, displayTimezone);
  return convertToTimezone(utcDate, practitionerTimezone);
};

/**
 * Convert a local browser time to Cliniko API format (ISO string)
 * Cliniko expects UTC times but interprets them in the business timezone
 * @param date - Local date
 * @param practitionerTimezone - Practitioner's timezone
 * @returns ISO string for Cliniko API
 */
export const convertToClinikoFormat = (
  date: Date,
  practitionerTimezone: TimezoneValue
): string => {
  // Treat the local date as if it's in the practitioner's timezone
  // Then convert to UTC for Cliniko
  const practitionerDate = zonedTimeToUtc(date, practitionerTimezone);
  return practitionerDate.toISOString();
};

/**
 * Convert from Cliniko API format to local display time
 * @param isoString - ISO string from Cliniko API
 * @param practitionerTimezone - Practitioner's timezone
 * @returns Date object for local display
 */
export const convertFromClinikoFormat = (
  isoString: string,
  practitionerTimezone: TimezoneValue
): Date => {
  const utcDate = parseISO(isoString);
  return utcToZonedTime(utcDate, practitionerTimezone);
};

/**
 * Get the timezone offset for a specific timezone at a specific date
 * @param timezone - Target timezone
 * @param date - Date to check (for DST calculations)
 * @returns Offset in minutes
 */
export const getTimezoneOffset = (timezone: TimezoneValue, date: Date = new Date()): number => {
  const utcDate = new Date(date.getTime());
  const zonedDate = utcToZonedTime(utcDate, timezone);
  return zonedDate.getTimezoneOffset();
};

/**
 * Check if two dates are the same when accounting for timezone
 * @param date1 - First date
 * @param timezone1 - First date's timezone
 * @param date2 - Second date
 * @param timezone2 - Second date's timezone
 * @returns True if dates represent the same moment
 */
export const isSameMoment = (
  date1: Date,
  timezone1: TimezoneValue,
  date2: Date,
  timezone2: TimezoneValue
): boolean => {
  const utc1 = convertFromTimezone(date1, timezone1);
  const utc2 = convertFromTimezone(date2, timezone2);
  return utc1.getTime() === utc2.getTime();
};

/**
 * Format appointment time for display with timezone indicator
 * @param date - Appointment date
 * @param timezone - Timezone
 * @param includeTimezone - Whether to include timezone abbreviation
 * @returns Formatted string like "2:30 PM (ACDT)" or "2:30 PM"
 */
export const formatAppointmentTime = (
  date: Date,
  timezone: TimezoneValue,
  includeTimezone: boolean = true
): string => {
  const timeStr = formatInTimezone(date, timezone, 'h:mm a');
  
  if (!includeTimezone) {
    return timeStr;
  }
  
  // Get timezone abbreviation
  const zoneName = TIMEZONE_DISPLAY_NAMES[timezone];
  return `${timeStr} (${zoneName})`;
};

/**
 * Create a date object from time components in a specific timezone
 * @param year - Year
 * @param month - Month (0-11)
 * @param day - Day of month
 * @param hour - Hour (0-23)
 * @param minute - Minute (0-59)
 * @param timezone - Target timezone
 * @returns Date object in the specified timezone
 */
export const createDateInTimezone = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: TimezoneValue
): Date => {
  // Create date in local time first
  const localDate = new Date(year, month, day, hour, minute, 0, 0);
  
  // Convert to the target timezone
  return zonedTimeToUtc(localDate, timezone);
};

/**
 * Validate if a timezone is supported
 * @param timezone - Timezone string to validate
 * @returns True if timezone is supported
 */
export const isValidTimezone = (timezone: string): timezone is TimezoneValue => {
  return Object.values(TIMEZONES).includes(timezone as TimezoneValue);
};

/**
 * Get the default timezone for a practitioner
 * Falls back to Adelaide if practitioner timezone is not found
 * @param practitionerTimezone - Practitioner's timezone or undefined
 * @returns Valid timezone value
 */
export const getValidTimezone = (practitionerTimezone?: string): TimezoneValue => {
  if (practitionerTimezone && isValidTimezone(practitionerTimezone)) {
    return practitionerTimezone;
  }
  return DEFAULT_TIMEZONE;
};

/**
 * Convert business hours to practitioner timezone
 * @param startHour - Business start hour (24h format)
 * @param endHour - Business end hour (24h format)
 * @param businessTimezone - Business timezone
 * @param practitionerTimezone - Practitioner timezone
 * @param date - Date for DST calculations
 * @returns Adjusted business hours for practitioner timezone
 */
export const convertBusinessHours = (
  startHour: number,
  endHour: number,
  businessTimezone: TimezoneValue,
  practitionerTimezone: TimezoneValue,
  date: Date = new Date()
): { startHour: number; endHour: number } => {
  if (businessTimezone === practitionerTimezone) {
    return { startHour, endHour };
  }
  
  // Create dates for start and end times in business timezone
  const startDate = createDateInTimezone(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    startHour,
    0,
    businessTimezone
  );
  
  const endDate = createDateInTimezone(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    endHour,
    0,
    businessTimezone
  );
  
  // Convert to practitioner timezone
  const practitionerStart = convertToTimezone(startDate, practitionerTimezone);
  const practitionerEnd = convertToTimezone(endDate, practitionerTimezone);
  
  return {
    startHour: practitionerStart.getHours(),
    endHour: practitionerEnd.getHours()
  };
};